import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./ConditionCard.css";
import { RULE_OPTIONS } from "./conditions";
import {
  RuleType,
  PersonaCondition,
  AttributeConfig,
  AttributeConfigData,
  Employee,
} from "winslow-core";
import { useAttributeSuggestions } from "../../hooks/useEmployeeCache";

export interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: string; label: string; category?: string }[];
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
}) => {
  // Group options by category for better organization
  const groupedOptions = options.reduce(
    (acc, option) => {
      const category = option.category || "other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(option);
      return acc;
    },
    {} as Record<string, typeof options>
  );

  return (
    <div className="condition-card-select-wrapper">
      <label className="condition-card-select-label">{label}</label>
      <div className="condition-card-select-container">
        <select
          className="condition-card-select-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}>
          <option value="">Select</option>
          {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
            <optgroup
              key={category}
              label={category.charAt(0).toUpperCase() + category.slice(1)}>
              {categoryOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
};

/**
 * Enhanced AttributeSelect component that works with dynamic attribute discovery
 */
export interface AttributeSelectProps {
  label: string;
  value: string;
  onChange: (attributePath: string) => void;
  attributeConfig: AttributeConfigData;
  allowedCategories?: string[];
}

const AttributeSelect: React.FC<AttributeSelectProps> = ({
  label,
  value,
  onChange,
  attributeConfig,
  allowedCategories,
}) => {
  const { getAvailableAttributePaths } = useAttributeSuggestions();

  // Flatten all attributes into a single list with category labels
  const allAttributeOptions = useMemo(() => {
    const options: {
      id: string;
      label: string;
      category: string;
      hasValues: boolean;
    }[] = [];
    const availablePaths = getAvailableAttributePaths(); // Get paths that actually have data

    Object.entries(attributeConfig).forEach(([category, attributes]) => {
      // Skip categories that are not in the allowed list (if specified)
      if (allowedCategories && !allowedCategories.includes(category)) {
        return;
      }

      const categoryLabel =
        category.charAt(0).toUpperCase() +
        category.slice(1).replace(/([A-Z])/g, " $1");

      attributes.forEach((attr: any) => {
        const hasValues = availablePaths.includes(attr.path);
        options.push({
          id: attr.path,
          label: `${categoryLabel}: ${attr.displayName}${!hasValues ? " (no values)" : ""}`,
          category: categoryLabel,
          hasValues,
        });
      });
    });

    // Sort by category first, then by display name
    return options.sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return a.label.localeCompare(b.label);
    });
  }, [attributeConfig, allowedCategories, getAvailableAttributePaths]);

  return (
    <div className="condition-card-select-wrapper">
      <label className="condition-card-select-label">{label}</label>
      <div className="condition-card-select-container">
        <select
          className="condition-card-select-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}>
          <option value="">Select Attribute</option>
          {allAttributeOptions.map((option) => (
            <option
              key={option.id}
              value={option.hasValues ? option.id : ""}
              disabled={!option.hasValues}
              className={option.hasValues ? "" : "no-values"}
              style={{
                color: option.hasValues ? "inherit" : "#999",
                fontStyle: option.hasValues ? "normal" : "italic",
              }}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export interface ConditionCardProps {
  index: number;
  condition: PersonaCondition;
  onChange: (condition: PersonaCondition) => void;
  onDelete: (index: number) => void;
  attributeConfig?: AttributeConfigData; // Optional for dynamic discovery
  employees: Employee[];
  allowedCategories?: string[]; // Categories to show in the selector
}

export const ConditionCard: React.FC<ConditionCardProps> = ({
  index,
  condition,
  onChange,
  onDelete,
  attributeConfig,
  employees,
  allowedCategories,
}) => {
  const { getSuggestions } = useAttributeSuggestions();

  const [attributePath, setAttributePath] = useState<string>(
    condition.attributePath || ""
  );
  const [rule, setRule] = useState<string>(condition.rule || "");
  const [value, setValue] = useState<string>(condition.value || "");
  const [displayName, setDisplayName] = useState<string>(
    condition.displayName || ""
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get complete attribute configuration (predefined + discovered)
  const completeAttributeConfig = useMemo(() => {
    if (attributeConfig) {
      return attributeConfig;
    }

    return AttributeConfig.getAvailableAttributes();
  }, [attributeConfig]);

  // Use refs to track previous condition values to prevent unnecessary updates
  const prevCondition = useRef<PersonaCondition>(condition);

  useEffect(() => {
    const prev = prevCondition.current;
    let hasChanged = false;

    if (condition.attributePath !== prev.attributePath) {
      setAttributePath(condition.attributePath || "");
      hasChanged = true;
    }
    if (condition.rule !== prev.rule) {
      setRule(condition.rule || "");
      hasChanged = true;
    }
    if (condition.value !== prev.value) {
      setValue(condition.value || "");
      hasChanged = true;
    }
    if (condition.displayName !== prev.displayName) {
      setDisplayName(condition.displayName || "");
      hasChanged = true;
    }

    if (hasChanged) {
      prevCondition.current = condition;
    }
  }, [condition]);

  // Update display name when attribute path changes
  useEffect(() => {
    if (attributePath && completeAttributeConfig) {
      for (const attributes of Object.values(completeAttributeConfig)) {
        const foundAttr = attributes.find(
          (attr: any) => attr.path === attributePath
        );
        if (foundAttr && foundAttr.displayName !== displayName) {
          setDisplayName(foundAttr.displayName);
          break;
        }
      }
    }
  }, [attributePath, completeAttributeConfig, displayName]);

  // Memoize the condition object to prevent unnecessary re-renders
  const currentCondition = useMemo(
    () => ({
      attributePath,
      rule: rule as RuleType,
      value,
      displayName: displayName || undefined,
    }),
    [attributePath, rule, value, displayName]
  );

  // Use useRef to track the last sent condition to prevent loops
  const lastSentCondition = useRef<PersonaCondition | null>(null);

  useEffect(() => {
    // Only call onChange if the condition actually changed
    if (
      !lastSentCondition.current ||
      lastSentCondition.current.attributePath !==
        currentCondition.attributePath ||
      lastSentCondition.current.rule !== currentCondition.rule ||
      lastSentCondition.current.value !== currentCondition.value ||
      lastSentCondition.current.displayName !== currentCondition.displayName
    ) {
      lastSentCondition.current = currentCondition;
      onChange(currentCondition);
    }
  }, [currentCondition, onChange]);

  // Fetch suggestions for the current attribute path
  const fetchSuggestions = useCallback(
    (attributePath: string, searchTerm: string = "") => {
      if (!attributePath) {
        console.warn("Cannot fetch suggestions: missing attributePath");
        return;
      }

      setLoadingSuggestions(true);
      try {
        const data = getSuggestions(attributePath, searchTerm);
        setSuggestions(data || []);
      } catch (error) {
        console.error("Error getting suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [getSuggestions]
  );

  // Debounced suggestion fetching with proper cleanup
  const timeoutRef = useRef<NodeJS.Timeout>();

  const debouncedFetchSuggestions = useCallback(
    (attributePath: string, searchTerm: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        fetchSuggestions(attributePath, searchTerm);
      }, 300);
    },
    [fetchSuggestions]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Fetch suggestions when attribute path changes or when input is focused
  useEffect(() => {
    if (showSuggestions && attributePath) {
      debouncedFetchSuggestions(attributePath, value);
    }
  }, [showSuggestions, attributePath, value, debouncedFetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAttributeChange = (newAttributePath: string) => {
    setAttributePath(newAttributePath);
    // Reset suggestions when attribute changes
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setValue(suggestion);
    setShowSuggestions(false);
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    if (newValue.trim() && attributePath) {
      debouncedFetchSuggestions(attributePath, newValue);
    }
  };

  const handleValueFocus = () => {
    setShowSuggestions(true);
    if (attributePath && !suggestions.length && !loadingSuggestions) {
      fetchSuggestions(attributePath, value);
    }
  };

  if (!completeAttributeConfig) {
    return (
      <div className="condition-card-container">
        <div className="condition-card-card">
          <div className="condition-card-content">
            <p>Loading available attributes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="condition-card-container">
      <div className="condition-card-card">
        <div className="condition-card-content">
          <div className="condition-card-selection-row">
            <AttributeSelect
              label="Attribute"
              value={attributePath}
              onChange={handleAttributeChange}
              attributeConfig={completeAttributeConfig}
              allowedCategories={allowedCategories}
            />
            <SelectField
              label="Rule"
              value={rule}
              onChange={setRule}
              options={RULE_OPTIONS}
            />
          </div>
          <div className="condition-card-value-section">
            <label className="condition-card-value-label">Value</label>
            <div
              className="condition-card-value-container"
              style={{ position: "relative" }}>
              <input
                type="text"
                className="condition-card-value-input"
                placeholder="Enter value..."
                value={value}
                onChange={(e) => handleValueChange(e.target.value)}
                onFocus={handleValueFocus}
                aria-label="Value input"
              />
              {showSuggestions && attributePath && (
                <div
                  ref={suggestionsRef}
                  className="condition-card-suggestions-dropdown"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderTop: "none",
                    borderRadius: "0 0 4px 4px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}>
                  {loadingSuggestions ? (
                    <div
                      style={{
                        padding: "8px 12px",
                        color: "#666",
                        fontStyle: "italic",
                      }}>
                      Loading suggestions...
                    </div>
                  ) : suggestions.length > 0 ? (
                    suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom:
                            idx < suggestions.length - 1
                              ? "1px solid #eee"
                              : "none",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}>
                        {suggestion}
                      </div>
                    ))
                  ) : value.trim() ? (
                    <div
                      style={{
                        padding: "8px 12px",
                        color: "#666",
                        fontStyle: "italic",
                      }}>
                      No suggestions found
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "8px 12px",
                        color: "#666",
                        fontStyle: "italic",
                      }}>
                      Type to search for values...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className="condition-card-delete-button"
            aria-label="Delete condition">
            <span onClick={() => onDelete(index)}>Delete Condition</span>
          </div>
        </div>
      </div>
    </div>
  );
};
